import { useEffect, useRef, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquarePlus, Star, X, ImagePlus, Loader2, Send, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { markPromptDone } from "@/lib/feedbackBus";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "bug", label: "Bug" }, { key: "feature_request", label: "Feature request" },
  { key: "suggestion", label: "Suggestion" }, { key: "confusing", label: "Confusing" },
  { key: "unrealistic", label: "Unrealistic" }, { key: "compliment", label: "Compliment" },
  { key: "general", label: "General" },
];
const RECOMMEND = [["definitely", "Definitely"], ["probably", "Probably"], ["maybe", "Maybe"], ["probably_not", "Probably not"], ["definitely_not", "Definitely not"]];
const PAGE_NAMES = [
  [/^\/workspace\//, "Workspace"], [/^\/hq/, "HQ"], [/^\/portfolio/, "Portfolio"],
  [/^\/analytics/, "Analytics"], [/^\/interview/, "Interview"], [/^\/resume/, "Resume"],
  [/^\/admin/, "Admin"], [/^\/onboarding/, "Onboarding"], [/^\/pricing/, "Pricing"], [/^\//, "Home"],
];

function detectBrowser() {
  const ua = navigator.userAgent;
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Chromium/.test(ua)) return "Chrome";
  if (/Safari\//.test(ua) && !/Chrome/.test(ua)) return "Safari";
  if (/Firefox\//.test(ua)) return "Firefox";
  return "Other";
}

function Stars({ value, onChange, testidPrefix }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button key={n} type="button" data-testid={`${testidPrefix}-${n}`} onClick={() => onChange(n)}
          className="transition-transform duration-150 hover:scale-110">
          <Star className={`w-7 h-7 ${n <= value ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
        </button>
      ))}
    </div>
  );
}

export default function FeedbackWidget() {
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [fabHidden, setFabHidden] = useState(false);
  const [rating, setRating] = useState(0);
  const [category, setCategory] = useState("general");
  const [text, setText] = useState("");
  const [shotPath, setShotPath] = useState(null);
  const [shotPreview, setShotPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recommend, setRecommend] = useState(null);
  const [tName, setTName] = useState("");
  const [tRole, setTRole] = useState("");
  const [tQuote, setTQuote] = useState("");
  const [tConsent, setTConsent] = useState(false);
  const fileRef = useRef(null);

  // contextual prompt
  const [prompt, setPrompt] = useState(null); // {trigger,title,question,simId}
  const [pRealism, setPRealism] = useState(0);
  const [pText, setPText] = useState("");
  const [pBusy, setPBusy] = useState(false);

  const flagOn = !!user?.features?.beta_feedback;

  const captureContext = useCallback(() => {
    const path = location.pathname;
    const page = (PAGE_NAMES.find(([re]) => re.test(path)) || [, "Home"])[1];
    return {
      page, route: path, feature: page,
      browser: detectBrowser(),
      device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop",
      client_ts: new Date().toISOString(),
    };
  }, [location.pathname]);

  const currentSimId = () => {
    const m = location.pathname.match(/\/workspace\/([^/]+)/);
    return m ? m[1] : null;
  };

  // session heartbeat for analytics (runs for any logged-in user)
  useEffect(() => {
    if (!user?.user_id) return;
    const ping = () => api.post("/events/ping").catch(() => {});
    ping();
    const iv = setInterval(ping, 60000);
    return () => clearInterval(iv);
  }, [user?.user_id]);

  // listen for programmatic open + contextual prompts
  useEffect(() => {
    const onOpen = (e) => { if (e.detail?.category) setCategory(e.detail.category); setOpen(true); };
    const onPrompt = (e) => {
      if (!flagOn) return;
      setPrompt(e.detail); setPRealism(0); setPText("");
    };
    const onFab = (e) => setFabHidden(!!e.detail?.hidden);
    window.addEventListener("pmfeedback:open", onOpen);
    window.addEventListener("pmfeedback:prompt", onPrompt);
    window.addEventListener("pmfeedback:fab", onFab);
    return () => { window.removeEventListener("pmfeedback:open", onOpen); window.removeEventListener("pmfeedback:prompt", onPrompt); window.removeEventListener("pmfeedback:fab", onFab); };
  }, [flagOn]);

  const reset = () => { setRating(0); setCategory("general"); setText(""); setShotPath(null); setShotPreview(null); setRecommend(null); setTName(""); setTRole(""); setTQuote(""); setTConsent(false); };

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setShotPreview(URL.createObjectURL(f));
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/feedback/screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setShotPath(r.data.path);
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); setShotPreview(null); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!rating && text.trim().length < 3) { toast.error("Add a rating or a few words first."); return; }
    setBusy(true);
    try {
      await api.post("/feedback", {
        rating: rating || null, category, text: text.trim(), screenshot_path: shotPath,
        recommend, trigger: "manual", context: captureContext(), sim_id: currentSimId(),
      });
      if (tQuote.trim().length >= 10 && tConsent) {
        await api.post("/testimonials", {
          name: tName.trim(), role: tRole.trim(), quote: tQuote.trim(),
          consent: true, rating: rating || null, recommend,
        }).catch(() => {});
      }
      toast.success("Thank you — your feedback shapes the product.");
      setOpen(false); reset();
    } catch (e) { toast.error("Could not send feedback."); }
    finally { setBusy(false); }
  };

  const sendPrompt = async () => {
    if (!pRealism && pText.trim().length < 3) { closePrompt(true); return; }
    setPBusy(true);
    try {
      await api.post("/feedback", {
        rating: pRealism || null, realism: pRealism || null,
        category: pRealism && pRealism <= 2 ? "unrealistic" : "general",
        text: pText.trim(), trigger: "realism",
        prompt: { question: prompt.question, stage: prompt.title },
        context: captureContext(), sim_id: prompt.simId || currentSimId(),
      });
      toast.success("Thanks for the realism check!");
      closePrompt(true);
    } catch (e) { toast.error("Could not send."); }
    finally { setPBusy(false); }
  };

  const closePrompt = (done) => {
    if (done && prompt) markPromptDone(prompt.simId, prompt.trigger);
    setPrompt(null);
  };

  if (!flagOn) return null;

  return (
    <>
      {/* Floating button (hidden when merged into the PM Copilot menu) */}
      {!fabHidden && (
        <button data-testid="feedback-fab" onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-volt text-white shadow-lg shadow-volt/30 hover:bg-volt/90 transition-all duration-200 hover:scale-105 px-4 py-3 md:px-5">
          <MessageSquarePlus className="w-5 h-5" />
          <span className="hidden md:inline text-sm font-medium">Feedback</span>
        </button>
      )}

      {/* Contextual prompt card */}
      <AnimatePresence>
        {prompt && (
          <motion.div data-testid="feedback-prompt" initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.96 }}
            className="fixed bottom-24 right-5 z-[60] w-[320px] rounded-xl border border-volt/30 bg-card shadow-xl p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-1.5 text-volt"><Sparkles className="w-4 h-4" /><span className="overline">Quick check</span></div>
              <button data-testid="prompt-skip" onClick={() => closePrompt(true)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <p className="font-heading font-bold text-sm mt-2 leading-snug">{prompt.title}</p>
            {prompt.question && <p className="text-xs text-muted-foreground mt-1">{prompt.question}</p>}
            <div className="mt-3"><Stars value={pRealism} onChange={setPRealism} testidPrefix="prompt-star" /></div>
            <Textarea data-testid="prompt-text" value={pText} onChange={(e) => setPText(e.target.value)} rows={2}
              placeholder="What would make this feel more like a real workplace?" className="mt-3 text-sm" />
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button variant="ghost" size="sm" onClick={() => closePrompt(true)}>Skip</Button>
              <Button data-testid="prompt-send" size="sm" disabled={pBusy} onClick={sendPrompt} className="bg-volt hover:bg-volt/90 text-white rounded-full">
                {pBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send <Send className="w-3.5 h-3.5 ml-1" /></>}
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Feedback modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="feedback-modal" className="max-w-md max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><MessageSquarePlus className="w-5 h-5 text-volt" /> Share your feedback</DialogTitle>
            <DialogDescription>Help us make PM LaunchPad feel like a real job. We capture your current screen automatically.</DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div>
              <p className="overline text-muted-foreground mb-2">Overall experience</p>
              <Stars value={rating} onChange={setRating} testidPrefix="feedback-star" />
            </div>
            <div>
              <p className="overline text-muted-foreground mb-2">Category</p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c.key} data-testid={`feedback-cat-${c.key}`} onClick={() => setCategory(c.key)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${category === c.key ? "border-volt bg-volt/10 text-volt" : "border-border text-muted-foreground hover:border-volt/50"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
            <Textarea data-testid="feedback-text" value={text} onChange={(e) => setText(e.target.value)} rows={4}
              placeholder="Tell us what happened or how we can improve." />
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} data-testid="feedback-file-input" />
              {shotPreview ? (
                <div className="flex items-center gap-3">
                  <img src={shotPreview} alt="screenshot" className="w-14 h-14 rounded-md object-cover border border-border" />
                  <span className="text-xs text-muted-foreground">{uploading ? "Uploading…" : "Attached"}</span>
                  <button onClick={() => { setShotPath(null); setShotPreview(null); }} className="text-xs text-blaze">Remove</button>
                </div>
              ) : (
                <button data-testid="feedback-screenshot" onClick={() => fileRef.current?.click()}
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-volt transition-colors duration-200">
                  <ImagePlus className="w-4 h-4" /> Attach a screenshot (optional)
                </button>
              )}
            </div>
            <div>
              <p className="overline text-muted-foreground mb-2">Would you recommend PM LaunchPad AI?</p>
              <div className="flex flex-wrap gap-2">
                {RECOMMEND.map(([k, l]) => (
                  <button key={k} data-testid={`feedback-nps-${k}`} onClick={() => { setRecommend(k); if (["definitely", "probably"].includes(k) && !tName) setTName(user?.name || ""); }}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${recommend === k ? "border-volt bg-volt/10 text-volt" : "border-border text-muted-foreground hover:border-volt/50"}`}>
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {["definitely", "probably"].includes(recommend) && (
              <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                className="rounded-lg border border-volt/30 bg-volt/5 p-4 space-y-2.5" data-testid="testimonial-block">
                <p className="text-sm font-medium flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-volt" /> Leave a testimonial?</p>
                <p className="text-xs text-muted-foreground">With your permission we may feature it on our site to help other aspiring PMs.</p>
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="testimonial-name" value={tName} onChange={(e) => setTName(e.target.value)} placeholder="Your name" className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm" />
                  <input data-testid="testimonial-role" value={tRole} onChange={(e) => setTRole(e.target.value)} placeholder="Role (e.g. Aspiring PM)" className="rounded-md border border-border bg-background px-2.5 py-1.5 text-sm" />
                </div>
                <Textarea data-testid="testimonial-quote" value={tQuote} onChange={(e) => setTQuote(e.target.value)} rows={3} placeholder="What has PM LaunchPad done for you?" className="text-sm" />
                <label className="flex items-center gap-2 text-xs text-muted-foreground"><input type="checkbox" data-testid="testimonial-consent" checked={tConsent} onChange={(e) => setTConsent(e.target.checked)} /> You may display this publicly with my name.</label>
              </motion.div>
            )}
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground font-mono">
              <Sparkles className="w-3 h-3" /> Capturing: {captureContext().page} · {detectBrowser()} · {captureContext().device}
            </div>
            <Button data-testid="feedback-submit" disabled={busy || uploading} onClick={submit} className="w-full rounded-full bg-volt hover:bg-volt/90 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />} Send feedback
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
